/* =============================================================
   Admin Dashboard — United Dream Mongolia
   Tabs: Quick Orders (from OrderModal) | Applications | Callbacks
   ============================================================= */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl, getLoginUrlWithReturn } from "@/const";
import {
  Users,
  FileText,
  CheckCircle2,
  Clock,
  Globe,
  Search,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Edit3,
  Save,
  X,
  TrendingUp,
  Shield,
  ShoppingCart,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  RefreshCw,
  Trash2,
  Plus,
  UserPlus,
  Send,
  KeyRound,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { useLocation } from "wouter";

/* ── Status configs ──────────────────────────────────────── */
const APP_STATUS_OPTIONS = [
  { value: "pending", label: "Хүлээгдэж байна", color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/20" },
  { value: "reviewing", label: "Шалгаж байна", color: "text-blue-400", bg: "bg-blue-400/10 border-blue-400/20" },
  { value: "docs_needed", label: "Баримт бичиг шаардлагатай", color: "text-orange-400", bg: "bg-orange-400/10 border-orange-400/20" },
  { value: "processing", label: "Боловсруулж байна", color: "text-purple-400", bg: "bg-purple-400/10 border-purple-400/20" },
  { value: "submitted", label: "Илгээгдсэн", color: "text-cyan-400", bg: "bg-cyan-400/10 border-cyan-400/20" },
  { value: "approved", label: "Зөвшөөрөгдсөн", color: "text-green-400", bg: "bg-green-400/10 border-green-400/20" },
  { value: "rejected", label: "Татгалзсан", color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  { value: "completed", label: "Дууссан", color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/20" },
];

const ORDER_STATUS_OPTIONS = [
  { value: "pending", label: "Хүлээгдэж байна", color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/20" },
  { value: "payment_pending", label: "Төлбөр хүлээгдэж байна", color: "text-orange-400", bg: "bg-orange-400/10 border-orange-400/20" },
  { value: "confirmed", label: "Баталгаажсан", color: "text-green-400", bg: "bg-green-400/10 border-green-400/20" },
  { value: "cancelled", label: "Цуцлагдсан", color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
];

function StatusBadge({ status, options }: { status: string; options: typeof APP_STATUS_OPTIONS }) {
  const cfg = options.find((s) => s.value === status) || options[0];
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${cfg!.bg} ${cfg!.color}`}>
      {cfg!.label}
    </span>
  );
}

/* ── Quick Order Row ─────────────────────────────────────── */
function OrderRow({ order, onUpdate }: { order: any; onUpdate: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newStatus, setNewStatus] = useState(order.status);

  const updateMutation = trpc.orders.updateStatus.useMutation({
    onSuccess: () => { setEditing(false); onUpdate(); },
  });

  const deleteMutation = trpc.orders.delete.useMutation({
    onSuccess: () => onUpdate(),
  });

  return (
    <motion.div layout className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <ShoppingCart className="w-4 h-4 text-green-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-semibold text-sm truncate">{order.name}</span>
                <span className="text-gray-500 text-xs">#{order.id}</span>
              </div>
              <div className="text-gray-400 text-xs">
                {order.country} · {order.visaName}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge status={order.status} options={ORDER_STATUS_OPTIONS} />
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {[
                  { label: "Утас", value: order.phone, icon: Phone },
                  { label: "И-мэйл", value: order.email || "—", icon: Mail },
                  { label: "Насанд хүрсэн", value: `${order.adultCount} хүн`, icon: Users },
                  { label: "Хүүхэд", value: order.childCount > 0 ? `${order.childCount} хүүхэд` : "—", icon: Users },
                  { label: "Нийт дүн", value: `${order.totalPrice} ${order.currency}`, icon: DollarSign },
                  ...(order.couponCode ? [
                    { label: "Купон", value: `${order.couponCode} (-${order.discountPercent}%)`, icon: DollarSign },
                    { label: "Хөнгөлсөн дүн", value: order.discountedPrice || "—", icon: DollarSign },
                  ] : []),
                  { label: "Огноо", value: new Date(order.createdAt).toLocaleDateString("mn-MN"), icon: Calendar },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-2">
                    <item.icon className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-gray-500 text-xs">{item.label}</div>
                      <div className="text-white text-sm">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {editing ? (
                <div className="bg-white/5 rounded-xl p-4 space-y-3">
                  <label className="text-gray-400 text-xs mb-1.5 block">Статус өөрчлөх</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    {ORDER_STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value} className="bg-[#0A1628]">{s.label}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateMutation.mutate({ id: order.id, status: newStatus })}
                      disabled={updateMutation.isPending}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                    >
                      {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Хадгалах
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      className="flex items-center gap-2 text-gray-400 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      <X className="w-4 h-4" /> Болих
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
                  >
                    <Edit3 className="w-4 h-4" /> Статус засах
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`"${order.name}" захиалгыг устгах уу? Энэ үйлдлийг буцааж болохгүй.`)) {
                        deleteMutation.mutate({ id: order.id });
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Устгах
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Application Row ─────────────────────────────────────── */
function ApplicationRow({ app, onUpdate }: { app: any; onUpdate: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newStatus, setNewStatus] = useState(app.status);
  const [adminNote, setAdminNote] = useState(app.adminNote || "");

  const updateMutation = trpc.admin.updateStatus.useMutation({
    onSuccess: () => { setEditing(false); onUpdate(); },
  });

  return (
    <motion.div layout className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-4 h-4 text-blue-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-semibold text-sm truncate">
                  {app.lastName} {app.firstName}
                </span>
                <span className="text-gray-500 text-xs">#{app.id}</span>
              </div>
              <div className="text-gray-400 text-xs">
                {app.country} · {app.visaType}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge status={app.status} options={APP_STATUS_OPTIONS} />
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {[
                  { label: "Утас", value: app.phone },
                  { label: "И-мэйл", value: app.email },
                  { label: "Паспорт", value: app.passportNumber },
                  { label: "Зорилго", value: app.purpose },
                  { label: "Огноо", value: new Date(app.createdAt).toLocaleDateString("mn-MN") },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="text-gray-500 text-xs mb-0.5">{item.label}</div>
                    <div className="text-white">{item.value || "—"}</div>
                  </div>
                ))}
              </div>

              {editing ? (
                <div className="bg-white/5 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="text-gray-400 text-xs mb-1.5 block">Статус өөрчлөх</label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                    >
                      {APP_STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value} className="bg-[#0A1628]">{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1.5 block">Зөвлөхийн тэмдэглэл</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      rows={3}
                      placeholder="Тэмдэглэл..."
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateMutation.mutate({ id: app.id, status: newStatus, note: adminNote })}
                      disabled={updateMutation.isPending}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                    >
                      {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Хадгалах
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      className="flex items-center gap-2 text-gray-400 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      <X className="w-4 h-4" /> Болих
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  {app.adminNote && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 flex-1 mr-3">
                      <p className="text-blue-300 text-sm">
                        <span className="font-semibold">Тэмдэглэл:</span> {app.adminNote}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors ml-auto"
                  >
                    <Edit3 className="w-4 h-4" /> Засах
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Main Admin Page ─────────────────────────────────────── */
export default function Admin() {
  const { user, isAuthenticated, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<"orders" | "applications" | "callbacks" | "email" | "coupons" | "agents" | "consultations" | "contracts">("orders");
  const [consultSearch, setConsultSearch] = useState("");
  const [agentStatusFilter, setAgentStatusFilter] = useState("all");
  const [agentSearch, setAgentSearch] = useState("");
  const [sendingLinkId, setSendingLinkId] = useState<number | null>(null);
  const [sentLinkResult, setSentLinkResult] = useState<Record<number, string>>({});
  const [verifyingPaymentId, setVerifyingPaymentId] = useState<number | null>(null);
  const [paymentVerifyResults, setPaymentVerifyResults] = useState<Record<number, { paid: boolean; status: string; amount?: number | null; paidAt?: string | null }>>({});
  const [invidInputs, setInvidInputs] = useState<Record<number, string>>({});
  // Add agent dialog state
  const [showAddAgent, setShowAddAgent] = useState(false);
  // Agent email compose dialog state
  const [showAgentEmail, setShowAgentEmail] = useState(false);
  const [agentEmailRecipientType, setAgentEmailRecipientType] = useState<"all" | "active" | "new" | "link_sent">("all");
  const [agentEmailSubject, setAgentEmailSubject] = useState("");
  const [agentEmailBody, setAgentEmailBody] = useState("");
  const [agentEmailResult, setAgentEmailResult] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [agentEmailError, setAgentEmailError] = useState("");
  const [newAgent, setNewAgent] = useState<{ name: string; phone: string; email: string; plan: string; price: string; status: "new" | "link_sent" | "active" | "cancelled"; adminNote: string }>({ name: "", phone: "", email: "", plan: "", price: "", status: "new", adminNote: "" });
  const [addAgentError, setAddAgentError] = useState("");
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newCouponAgent, setNewCouponAgent] = useState("");
  const [newCouponEmail, setNewCouponEmail] = useState("");
  const [couponFormOpen, setCouponFormOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailFilter, setEmailFilter] = useState<"all" | "orders" | "applications" | "consultations">("all");
  const [sendResult, setSendResult] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [, navigate] = useLocation();

  const { data: orders, isLoading: ordersLoading, refetch: refetchOrders } = trpc.orders.list.useQuery(undefined, {
    enabled: isAuthenticated && user?.role === "admin",
  });

  const { data: applications, isLoading: appsLoading, refetch: refetchApps } = trpc.admin.listAll.useQuery(undefined, {
    enabled: isAuthenticated && user?.role === "admin",
  });

  const { data: callbackRequests, refetch: refetchCallbacks } = trpc.admin.callbackList.useQuery(undefined, {
    enabled: isAuthenticated && user?.role === "admin",
  });

  const { data: consultationList, refetch: refetchConsultations } = trpc.consultation.list.useQuery(
    { status: "all" },
    { enabled: isAuthenticated && user?.role === "admin" }
  );

  const { data: emailList } = trpc.admin.getEmailList.useQuery(undefined, {
    enabled: isAuthenticated && user?.role === "admin",
  });

  const { data: couponList, isLoading: couponsLoading, refetch: refetchCoupons } = trpc.coupons.list.useQuery(undefined, {
    enabled: isAuthenticated && user?.role === "admin",
  });

  const createCoupon = trpc.coupons.create.useMutation({ onSuccess: () => { refetchCoupons(); setCouponFormOpen(false); setNewCouponCode(""); setNewCouponAgent(""); setNewCouponEmail(""); } });
  const toggleCoupon = trpc.coupons.toggle.useMutation({ onSuccess: () => refetchCoupons() });
  const deleteCoupon = trpc.coupons.delete.useMutation({ onSuccess: () => refetchCoupons() });

  const { data: agentList, isLoading: agentsLoading, refetch: refetchAgents } = trpc.affiliate.listRegistrations.useQuery(
    { status: agentStatusFilter },
    { enabled: isAuthenticated && user?.role === "admin" }
  );
  const sendTrainingLink = trpc.affiliate.sendTrainingLink.useMutation({
    onSuccess: (data, variables) => {
      setSentLinkResult((prev) => ({ ...prev, [variables.id]: data.referralCode ?? "" }));
      setSendingLinkId(null);
      refetchAgents();
    },
    onError: () => setSendingLinkId(null),
  });
  const updateAgentStatus = trpc.affiliate.updateStatus.useMutation({ onSuccess: () => refetchAgents() });
  const deleteAgent = trpc.affiliate.delete.useMutation({ onSuccess: () => refetchAgents() });
  const [resetPasswordId, setResetPasswordId] = useState<number | null>(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<{id: number, password: string} | null>(null);
  const resetPasswordMutation = trpc.affiliate.resetPassword.useMutation({
    onSuccess: (data, variables) => {
      setResetPasswordId(null);
      setResetPasswordSuccess({ id: variables.id, password: data.newPassword });
      setTimeout(() => setResetPasswordSuccess(null), 10000);
      refetchAgents();
    },
    onError: () => setResetPasswordId(null),
  });
  const verifyAgentPayment = trpc.affiliate.verifyPayment.useMutation({
    onSuccess: (data, variables) => {
      setPaymentVerifyResults((prev) => ({ ...prev, [variables.id]: data }));
      setVerifyingPaymentId(null);
      if (data.paid) refetchAgents();
    },
    onError: (err, variables) => {
      setPaymentVerifyResults((prev) => ({ ...prev, [variables.id]: { paid: false, status: err.message } }));
      setVerifyingPaymentId(null);
    },
  });
  const createAgent = trpc.affiliate.create.useMutation({
    onSuccess: () => {
      refetchAgents();
      setShowAddAgent(false);
      setNewAgent({ name: "", phone: "", email: "", plan: "", price: "", status: "new", adminNote: "" });
      setAddAgentError("");
    },
    onError: (err) => setAddAgentError(err.message || "Алдаа гарлаалаа"),
  });

  const sendAgentEmail = trpc.affiliate.sendEmail.useMutation({
    onSuccess: (result) => {
      setAgentEmailResult(result);
    },
    onError: (err) => setAgentEmailError(err.message || "Алдаа гарлаалаа"),
  });

  const sendMassEmail = trpc.admin.sendMassEmail.useMutation<{ subject: string; htmlBody: string; recipientFilter: "all" | "orders" | "applications" | "consultations" }>({
    onSuccess: (result) => {
      setSendResult(result);
    },
  });

    // Contract hooks - must be before any early returns
  const [contractTemplateId, setContractTemplateId] = useState<number | null>(null);
  const [contractCountry, setContractCountry] = useState("usa");
  const [contractServiceName, setContractServiceName] = useState("");
  const [contractBodyHtml, setContractBodyHtml] = useState("");
  const [contractPaymentType, setContractPaymentType] = useState<"full" | "installment">("full");
  const [contractTotal, setContractTotal] = useState("");
  const [contractAdvance, setContractAdvance] = useState("");
  const [contractRemaining, setContractRemaining] = useState("");
  const [contractClientEmail, setContractClientEmail] = useState("");
  const [contractStatusFilter, setContractStatusFilter] = useState<"all" | "sent" | "signed" | "cancelled">("all");
  const [contractSent, setContractSent] = useState(false);
  const contractTemplateList = trpc.contracts.listTemplates.useQuery(undefined, { enabled: isAuthenticated && user?.role === "admin" });
  const contractList = trpc.contracts.list.useQuery({ status: contractStatusFilter }, { enabled: activeTab === "contracts" && isAuthenticated && user?.role === "admin" });
  const sendContractMutation = trpc.contracts.send.useMutation({
    onSuccess: () => {
      setContractSent(true);
      setContractClientEmail("");
      setContractServiceName("");
      setContractTotal("");
      setContractAdvance("");
      setContractRemaining("");
      contractList.refetch();
      setTimeout(() => setContractSent(false), 3000);
    },
  });
  const cancelContractMutation = trpc.contracts.cancel.useMutation({
    onSuccess: () => contractList.refetch(),
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A1628]">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated || user?.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A1628] text-white px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Хандах эрхгүй</h2>
          <p className="text-gray-400 mb-6">Энэ хуудас зөвхөн администраторт зориулагдсан.</p>
          {!isAuthenticated && (
            <a
              href={getLoginUrlWithReturn("/admin")}
              className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors"
            >
              Нэвтэрэх
            </a>
          )}
        </div>
      </div>
    );
  }

  // Filter helpers
  const filterOrders = (list: any[]) =>
    list.filter((o) => {
      const matchSearch = !search || `${o.name} ${o.phone} ${o.email ?? ""} ${o.country} ${o.visaName}`.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || o.status === statusFilter;
      return matchSearch && matchStatus;
    });

  const filterApps = (list: any[]) =>
    list.filter((a) => {
      const matchSearch = !search || `${a.firstName} ${a.lastName} ${a.email} ${a.phone} ${a.country}`.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || a.status === statusFilter;
      return matchSearch && matchStatus;
    });

  const filteredOrders = filterOrders(orders || []);
  const filteredApps = filterApps(applications || []);

  const stats = {
    orders: orders?.length || 0,
    ordersNew: orders?.filter((o: any) => o.status === "pending").length || 0,
    ordersConfirmed: orders?.filter((o: any) => o.status === "confirmed").length || 0,
    apps: applications?.length || 0,
    appsNew: applications?.filter((a: any) => a.status === "pending").length || 0,
    appsCompleted: applications?.filter((a: any) => ["approved", "completed"].includes(a.status)).length || 0,
    callbacks: callbackRequests?.length || 0,
  };

  const emailCount = emailList?.length || 0;

  const filteredConsultations = (consultationList || []).filter(c => {
    if (!consultSearch) return true;
    const q = consultSearch.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.phone.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });

  const CONSULT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
    new:              { label: "Шинэ",                  color: "text-blue-400" },
    phone_consulted:  { label: "Утсаар зөвлөгөө өгсөн", color: "text-green-400" },
    callback:         { label: "Дахин залгах",          color: "text-amber-400" },
    office_consulted: { label: "Оффист зөвлөгөө өгсөн", color: "text-purple-400" },
  };
  const CONSULT_COUNTRY_LABELS: Record<string, string> = {
    usa: "🇺🇸 АНУ", australia: "🇦🇺 Австрали", canada: "🇨🇦 Канад",
    ireland: "🇮🇪 Ирланд", uk: "🇬🇧 Их Британи", schengen: "🇪🇺 Шенген",
    czech: "🇨🇿 Чех", newzealand: "🇳🇿 Шинэ Зеланд", other: "🌍 Бусад",
  };

  const tabs = [
    { key: "orders", label: "Захиалгууд", count: stats.orders, icon: ShoppingCart, color: "text-green-400" },
    { key: "applications", label: "Өргөдлүүд", count: stats.apps, icon: FileText, color: "text-blue-400" },
    { key: "consultations", label: "Зөвлөгөө", count: consultationList?.length || 0, icon: Phone, color: "text-violet-400" },
    { key: "agents", label: "Агентүүд", count: agentList?.length || 0, icon: Users, color: "text-amber-400" },
    { key: "contracts", label: "Гэрээ", count: contractList.data?.length || 0, icon: FileText, color: "text-blue-300" },
    { key: "callbacks", label: "Буцаан залгах", count: stats.callbacks, icon: Phone, color: "text-purple-400" },
    { key: "email", label: "Mass Email", count: emailCount, icon: Mail, color: "text-cyan-400" },
    { key: "coupons", label: "Купон код", count: couponList?.length || 0, icon: TrendingUp, color: "text-emerald-400" },
  ] as const;

  const currentStatusOptions = activeTab === "orders" ? ORDER_STATUS_OPTIONS : APP_STATUS_OPTIONS;

  const handleRefresh = () => {
    refetchOrders();
    refetchApps();
    refetchCallbacks();
    refetchCoupons();
    refetchAgents();
    refetchConsultations();
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-[#0A1628] flex flex-col">
        <Header />

        <div className="flex-1 py-10 px-4">
          <div className="max-w-5xl mx-auto">
            {/* Page header */}
            <div className="mb-8 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-4 h-4 text-blue-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white">Админ хяналтын самбар</h1>
                </div>
                <p className="text-gray-400 text-sm ml-11">United Dream Mongolia — Захиалгын удирдлага</p>
              </div>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 text-gray-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 rounded-xl text-sm transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Шинэчлэх
              </button>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { label: "Нийт захиалга", value: stats.orders, color: "text-green-400", bg: "bg-green-400/10", icon: ShoppingCart },
                { label: "Шинэ захиалга", value: stats.ordersNew, color: "text-amber-400", bg: "bg-amber-400/10", icon: Clock },
                { label: "Нийт өргөдөл", value: stats.apps, color: "text-blue-400", bg: "bg-blue-400/10", icon: FileText },
                { label: "Амжилттай", value: stats.appsCompleted, color: "text-emerald-400", bg: "bg-emerald-400/10", icon: CheckCircle2 },
              ].map((s) => (
                <div key={s.label} className={`${s.bg} border border-white/10 rounded-2xl p-4`}>
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className={`w-4 h-4 ${s.color}`} />
                  </div>
                  <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-gray-400 text-xs mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white/5 rounded-2xl p-1 mb-6">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setSearch(""); setStatusFilter("all"); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.key
                      ? "bg-white/10 text-white"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  <tab.icon className={`w-4 h-4 ${activeTab === tab.key ? tab.color : ""}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? "bg-white/20 text-white" : "bg-white/10 text-gray-400"}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* CRM shortcut */}
            <button
              onClick={() => navigate("/staff/crm")}
              className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-semibold mb-4 transition-all"
              style={{
                background: "rgba(124,58,237,0.12)",
                border: "1px solid rgba(124,58,237,0.30)",
                color: "#a78bfa",
              }}
            >
              <Phone className="w-4 h-4" />
              Зөвлөгөө авах хүсэлт — Бүрэн харах (Staff CRM)
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(124,58,237,0.20)", color: "#c4b5fd" }}>CRM</span>
            </button>

            {/* DV registration shortcut */}
            <button
              onClick={() => navigate("/admin/dv")}
              className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-semibold mb-4 transition-all"
              style={{
                background: "rgba(16,185,129,0.12)",
                border: "1px solid rgba(16,185,129,0.30)",
                color: "#6ee7b7",
              }}
            >
              <KeyRound className="w-4 h-4" />
              Ногоон карт / DV Lottery — Бүртгэл, төлбөр, имэйл
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(16,185,129,0.20)", color: "#a7f3d0" }}>DV</span>
            </button>

            {/* Contracts shortcut */}
            <button
              onClick={() => navigate("/admin/contracts")}
              className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-semibold mb-4 transition-all"
              style={{
                background: "rgba(37,99,235,0.12)",
                border: "1px solid rgba(37,99,235,0.30)",
                color: "#60a5fa",
              }}
            >
              <FileText className="w-4 h-4" />
              Гэрээний систем — Гэрээ явуулах, засварлах
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(37,99,235,0.20)", color: "#93c5fd" }}>Гэрээ</span>
            </button>
            {/* Filters (for orders and applications) */}
            {activeTab !== "callbacks" && activeTab !== "email" && activeTab !== "consultations" && (
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Нэр, утас, имэйл, улс хайх..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 placeholder-gray-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="all" className="bg-[#0A1628]">Бүх статус</option>
                  {currentStatusOptions.map((s) => (
                    <option key={s.value} value={s.value} className="bg-[#0A1628]">{s.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Tab content */}
            <AnimatePresence mode="wait">
              {activeTab === "orders" && (
                <motion.div key="orders" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-white font-semibold">
                      Захиалгууд <span className="text-gray-400 font-normal text-sm">({filteredOrders.length})</span>
                    </h2>
                  </div>
                  {ordersLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                    </div>
                  ) : filteredOrders.length > 0 ? (
                    <div className="space-y-3">
                      {filteredOrders.map((order: any) => (
                        <OrderRow key={order.id} order={order} onUpdate={refetchOrders} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <AlertCircle className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Захиалга олдсонгүй</p>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === "applications" && (
                <motion.div key="applications" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-white font-semibold">
                      Өргөдлүүд <span className="text-gray-400 font-normal text-sm">({filteredApps.length})</span>
                    </h2>
                  </div>
                  {appsLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                    </div>
                  ) : filteredApps.length > 0 ? (
                    <div className="space-y-3">
                      {filteredApps.map((app: any) => (
                        <ApplicationRow key={app.id} app={app} onUpdate={refetchApps} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <AlertCircle className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Өргөдөл олдсонгүй</p>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === "email" && (
                <motion.div key="email" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="mb-6">
                    <h2 className="text-white font-semibold mb-1">Mass Email илгээх</h2>
                    <p className="text-gray-400 text-sm">Database-д байгаа бүх хэрэглэгчдэд нэг дор имэйл илгээх</p>
                  </div>

                  {/* Recipient filter */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-4 h-4 text-amber-400" />
                      <span className="text-white text-sm font-medium">Хүлээн авагчид</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {(["all", "orders", "applications", "consultations"] as const).map((f) => {
                        const consultationCount = emailList?.filter((r: any) => r.source === "consultation").length || 0;
                        const filterCount = f === "all" ? emailCount : f === "orders" ? emailList?.filter((r: any) => r.source === "order").length || 0 : f === "applications" ? emailList?.filter((r: any) => r.source === "application").length || 0 : consultationCount;
                        return (
                          <button
                            key={f}
                            onClick={() => setEmailFilter(f)}
                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                              emailFilter === f
                                ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                                : "bg-white/5 text-gray-400 border border-white/10 hover:text-white"
                            }`}
                          >
                            {f === "all" ? `Бүгд (${emailCount})` : f === "orders" ? "Захиалгаас" : f === "applications" ? "Өргөдлөөс" : `Зөвлөгөө (${filterCount})`}
                          </button>
                        );
                      })}
                    </div>
                    {emailList && emailList.length > 0 && (
                      <div className="mt-3 max-h-32 overflow-y-auto">
                        <div className="flex flex-wrap gap-1">
                          {emailList.filter((r) => {
                            if (emailFilter === "all") return true;
                            if (emailFilter === "orders") return r.source === "order";
                            if (emailFilter === "applications") return r.source === "application";
                            if (emailFilter === "consultations") return r.source === "consultation";
                            return false;
                          }).map((r) => (
                            <span key={r.email} className="text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-gray-400">
                              {r.name} · {r.email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Subject */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4">
                    <label className="text-white text-sm font-medium block mb-2">Гарчиг (Subject)</label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Имэйлийн гарчиг..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500 placeholder-gray-500"
                    />
                  </div>

                  {/* Body */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4">
                    <label className="text-white text-sm font-medium block mb-2">Агуулга (HTML эсвэл энгийн текст)</label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Имэйлийн агуулга... HTML ашиглаж болно."
                      rows={10}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-500 placeholder-gray-500 font-mono resize-y"
                    />
                  </div>

                  {/* Result */}
                  {sendResult && (
                    <div className={`rounded-2xl p-4 mb-4 ${
                      sendResult.failed === 0 ? "bg-green-500/10 border border-green-500/30" : "bg-amber-500/10 border border-amber-500/30"
                    }`}>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className={`w-5 h-5 ${sendResult.failed === 0 ? "text-green-400" : "text-amber-400"}`} />
                        <span className="text-white font-medium">
                          {sendResult.sent}/{sendResult.total} имэйл амжилттай илгээгдлээ
                          {sendResult.failed > 0 && ` · ${sendResult.failed} амжилтгүй`}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Send button */}
                  <button
                    onClick={() => {
                      if (!emailSubject.trim() || !emailBody.trim()) return;
                      setSendResult(null);
                      sendMassEmail.mutate({ subject: emailSubject, htmlBody: emailBody, recipientFilter: emailFilter });
                    }}
                    disabled={sendMassEmail.isPending || !emailSubject.trim() || !emailBody.trim()}
                    className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold py-3 rounded-2xl transition-colors"
                  >
                    {sendMassEmail.isPending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Илгээж байна...</>
                    ) : (
                      <><Mail className="w-4 h-4" /> {emailCount} хүнд имэйл илгээх</>
                    )}
                  </button>
                </motion.div>
              )}

              {activeTab === "callbacks" && (
                <motion.div key="callbacks" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="mb-4">
                    <h2 className="text-white font-semibold">
                      Буцаан залгах хүсэлтүүд <span className="text-gray-400 font-normal text-sm">({callbackRequests?.length || 0})</span>
                    </h2>
                  </div>
                  {callbackRequests && callbackRequests.length > 0 ? (
                    <div className="space-y-3">
                      {callbackRequests.map((req: any) => (
                        <div key={req.id} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-purple-500/20 rounded-xl flex items-center justify-center">
                                <Phone className="w-4 h-4 text-purple-400" />
                              </div>
                              <div>
                                <div className="text-white font-semibold">{req.name}</div>
                                <div className="text-gray-400 text-sm">
                                  <a href={`tel:${req.phone}`} className="hover:text-white transition-colors">{req.phone}</a>
                                  {req.country && ` · ${req.country}`}
                                </div>
                              </div>
                            </div>
                            <div className="text-gray-500 text-xs">
                              {new Date(req.createdAt).toLocaleDateString("mn-MN")}
                            </div>
                          </div>
                          {req.message && (
                            <div className="mt-3 bg-white/5 rounded-xl p-3">
                              <p className="text-gray-300 text-sm">{req.message}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <AlertCircle className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Хүсэлт олдсонгүй</p>
                    </div>
                  )}
                </motion.div>
              )}
              {activeTab === "coupons" && (
                <motion.div key="coupons" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-white font-semibold">Купон кодууд <span className="text-gray-400 font-normal text-sm">({couponList?.length || 0})</span></h2>
                      <p className="text-gray-400 text-sm mt-0.5">Affiliate агентуудын реферал код удирдан уу</p>
                    </div>
                    <button
                      onClick={() => setCouponFormOpen(!couponFormOpen)}
                      className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
                    >
                      <TrendingUp className="w-4 h-4" /> Шинэ код
                    </button>
                  </div>

                  {/* Create coupon form */}
                  {couponFormOpen && (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6 space-y-3">
                      <h3 className="text-white font-medium text-sm">Шинэ купон код үүсгэх</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Код (дээш үсэг автоматаар томордоно) *</label>
                          <input
                            type="text"
                            value={newCouponCode}
                            onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                            placeholder="Жишээ: AGENT001"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500 placeholder-gray-500 uppercase"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Агентын нэр</label>
                          <input
                            type="text"
                            value={newCouponAgent}
                            onChange={(e) => setNewCouponAgent(e.target.value)}
                            placeholder="Агентын нэр..."
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500 placeholder-gray-500"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Агентын имэйл</label>
                          <input
                            type="email"
                            value={newCouponEmail}
                            onChange={(e) => setNewCouponEmail(e.target.value)}
                            placeholder="agent@email.com"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500 placeholder-gray-500"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Хөнгөлөлтийн хувь (%) — өгчийлөр 5%</label>
                          <input
                            type="number"
                            defaultValue={5}
                            min={1}
                            max={100}
                            id="couponDiscountInput"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => {
                            if (!newCouponCode.trim()) return;
                            const discountInput = document.getElementById("couponDiscountInput") as HTMLInputElement;
                            createCoupon.mutate({
                              code: newCouponCode,
                              agentName: newCouponAgent || undefined,
                              agentEmail: newCouponEmail || undefined,
                              discountPercent: parseInt(discountInput?.value || "5", 10),
                            });
                          }}
                          disabled={createCoupon.isPending || !newCouponCode.trim()}
                          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
                        >
                          {createCoupon.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Үүсгэх
                        </button>
                        <button
                          onClick={() => setCouponFormOpen(false)}
                          className="flex items-center gap-2 text-gray-400 hover:text-white border border-white/10 px-4 py-2 rounded-xl text-sm transition-colors"
                        >
                          <X className="w-4 h-4" /> Болих
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Coupon list */}
                  {couponsLoading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
                  ) : couponList && couponList.length > 0 ? (
                    <div className="space-y-3">
                      {couponList.map((coupon: any) => (
                        <div key={coupon.id} className={`bg-white/5 border rounded-2xl p-4 transition-all ${
                          coupon.isActive ? "border-white/10" : "border-red-500/20 opacity-60"
                        }`}>
                          <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                                coupon.isActive ? "bg-emerald-500/20" : "bg-red-500/20"
                              }`}>
                                <TrendingUp className={`w-4 h-4 ${coupon.isActive ? "text-emerald-400" : "text-red-400"}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-bold font-mono text-base">{coupon.code}</span>
                                  <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">-{coupon.discountPercent}%</span>
                                  {!coupon.isActive && <span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full">Хүчингүй</span>}
                                </div>
                                <div className="text-gray-400 text-xs mt-0.5">
                                  {coupon.agentName && <span>{coupon.agentName}</span>}
                                  {coupon.agentEmail && <span className="ml-2 text-gray-500">{coupon.agentEmail}</span>}
                                  <span className="ml-2">Хэрэглэсэн: {coupon.usageCount} удаа</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCoupon.mutate({ id: coupon.id, isActive: coupon.isActive === 1 ? 0 : 1 })}
                                disabled={toggleCoupon.isPending}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  coupon.isActive
                                    ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
                                    : "bg-green-500/20 text-green-300 hover:bg-green-500/30"
                                }`}
                              >
                                {coupon.isActive ? "Идэвхжүүлэх" : "Идэвхжүүлэх"}
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm(`"${coupon.code}" кодыг устгах уу?`)) {
                                    deleteCoupon.mutate({ id: coupon.id });
                                  }
                                }}
                                disabled={deleteCoupon.isPending}
                                className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <TrendingUp className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Купон код байхгүй байна</p>
                      <p className="text-gray-500 text-sm mt-1">Дээрх товч дараах шинэ код үүсгээрэй</p>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === "agents" && (
                <motion.div key="agents" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-white font-semibold">Аффилиате агентууд <span className="text-gray-400 font-normal text-sm">({agentList?.length || 0})</span></h2>
                      <button
                        onClick={() => setShowAddAgent(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 text-xs font-medium rounded-lg transition-colors"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Агент нэмэх
                      </button>
                      <button
                        onClick={() => { setShowAgentEmail(true); setAgentEmailResult(null); setAgentEmailError(""); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 text-xs font-medium rounded-lg transition-colors"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Мэйл явуулах
                      </button>
                    </div>
                    {/* Status filter */}
                    <div className="flex gap-2 flex-wrap">
                      {["all", "new", "link_sent", "active", "cancelled"].map((s) => (
                        <button
                          key={s}
                          onClick={() => setAgentStatusFilter(s)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            agentStatusFilter === s
                              ? "bg-amber-500 text-white"
                              : "bg-white/10 text-gray-400 hover:bg-white/20"
                          }`}
                        >
                          {{ all: "Бүгд", new: "Шинэ", link_sent: "Линк явуулсан", active: "Идэвхтэй", cancelled: "Цуцлагдсан" }[s]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      value={agentSearch}
                      onChange={(e) => setAgentSearch(e.target.value)}
                      placeholder="Нэр, утас, имэйлеэр хайх..."
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/50"
                    />
                  </div>

                  {agentsLoading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>
                  ) : agentList && agentList.length > 0 ? (
                    <div className="space-y-3">
                      {agentList
                        .filter((a) => !agentSearch || `${a.name} ${a.phone} ${a.email}`.toLowerCase().includes(agentSearch.toLowerCase()))
                        .map((agent) => {
                          const statusColors: Record<string, string> = {
                            new: "text-amber-400 bg-amber-400/10 border-amber-400/20",
                            link_sent: "text-blue-400 bg-blue-400/10 border-blue-400/20",
                            active: "text-green-400 bg-green-400/10 border-green-400/20",
                            cancelled: "text-red-400 bg-red-400/10 border-red-400/20",
                          };
                          const statusLabels: Record<string, string> = {
                            new: "Шинэ",
                            link_sent: "Линк явуулсан",
                            active: "Идэвхтэй",
                            cancelled: "Цуцлагдсан",
                          };
                          const isSending = sendingLinkId === agent.id;
                          const justSent = sentLinkResult[agent.id];
                          return (
                            <div key={agent.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-amber-400/30 transition-colors">
                              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                {/* Agent info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-2">
                                    <span className="text-white font-semibold">{agent.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColors[agent.status] ?? statusColors.new}`}>
                                      {statusLabels[agent.status] ?? agent.status}
                                    </span>
                                    {agent.referralCode && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-400/10 border border-green-400/20 text-green-400 font-mono font-bold">
                                        {agent.referralCode}
                                      </span>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-sm">
                                    <span className="text-gray-400 flex items-center gap-1"><Phone className="w-3 h-3" />{agent.phone}</span>
                                    <span className="text-gray-400 flex items-center gap-1"><Mail className="w-3 h-3" />{agent.email}</span>
                                    <span className="text-gray-400 flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(agent.createdAt).toLocaleDateString("mn-MN")}</span>
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500">Төлөвлөгөө: {agent.plan} | Дүн: {agent.price}</div>
                                  {agent.linkSentAt && (
                                    <div className="mt-1 text-xs text-blue-400">Линк явуулсан: {new Date(agent.linkSentAt).toLocaleString("mn-MN")}</div>
                                  )}
                                  {/* Payment status */}
                                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    {agent.paymentVerified ? (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-400/10 border border-green-400/20 text-green-400 font-medium">
                                        💳 Төлбөр баталгаажсан {agent.paymentVerifiedAt ? `(${new Date(agent.paymentVerifiedAt).toLocaleDateString("mn-MN")})` : ""}
                                      </span>
                                    ) : (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-400/10 border border-red-400/20 text-red-400 font-medium">
                                        💳 Төлбөр баталгаажаагүй
                                      </span>
                                    )}
                                    {agent.paylinkInvid && (
                                      <span className="text-xs text-gray-500 font-mono">invid: {agent.paylinkInvid}</span>
                                    )}
                                  </div>
                                  {/* Paylink verify section */}
                                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    {!agent.paylinkInvid && (
                                      <input
                                        type="text"
                                        placeholder="Paylink invid оруулах..."
                                        value={invidInputs[agent.id] || ""}
                                        onChange={(e) => setInvidInputs((prev) => ({ ...prev, [agent.id]: e.target.value }))}
                                        className="bg-white/5 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400/50 w-44"
                                      />
                                    )}
                                    <button
                                      onClick={() => {
                                        setVerifyingPaymentId(agent.id);
                                        verifyAgentPayment.mutate({ id: agent.id, invid: invidInputs[agent.id] || undefined });
                                      }}
                                      disabled={verifyingPaymentId === agent.id}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                                    >
                                      {verifyingPaymentId === agent.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>💳</span>}
                                      Paylink шалгах
                                    </button>
                                    {paymentVerifyResults[agent.id] && (
                                      <span className={`text-xs font-medium ${paymentVerifyResults[agent.id].paid ? "text-green-400" : "text-red-400"}`}>
                                        {paymentVerifyResults[agent.id].paid
                                          ? `✅ Төлсөн${paymentVerifyResults[agent.id].amount ? ` (${paymentVerifyResults[agent.id].amount}₮)` : ""}`
                                          : `❌ ${paymentVerifyResults[agent.id].status}`}
                                      </span>
                                    )}
                                  </div>
                                  {justSent && (
                                    <div className="mt-2 text-xs text-green-400 font-medium">✅ Линк амжилттай явууллаа! Код: <span className="font-mono font-bold">{justSent}</span></div>
                                  )}
                                  {resetPasswordSuccess?.id === agent.id && (
                                    <div className="mt-2 text-xs text-blue-400 font-medium">🔑 Шинэ нууц үг: <span className="font-mono font-bold bg-blue-500/20 px-1 rounded">{resetPasswordSuccess.password}</span> — имэйлд явлаа</div>
                                  )}
                                </div>
                                {/* Actions */}
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        setSendingLinkId(agent.id);
                                        sendTrainingLink.mutate({ id: agent.id });
                                      }}
                                      disabled={isSending}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                                    >
                                      {isSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                      {agent.referralCode ? "Дахин явуулах" : "Линк явуулах"}
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (confirm(`"${agent.name}" агентын нууц үгийг шинэчлэх үү? Шинэ нууц үг имэйлд явна.`)) {
                                          setResetPasswordId(agent.id);
                                          resetPasswordMutation.mutate({ id: agent.id });
                                        }
                                      }}
                                      disabled={resetPasswordId === agent.id}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                                    >
                                      {resetPasswordId === agent.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
                                      Нууц үг шинэчлэх
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (confirm(`"${agent.name}" агентыг устгах уу? Энэ үйлдлийг буцааж болохгүй.`)) {
                                          deleteAgent.mutate({ id: agent.id });
                                        }
                                      }}
                                      disabled={deleteAgent.isPending}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                                    >
                                      {deleteAgent.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                      Устгах
                                    </button>
                                  </div>
                                  <select
                                    value={agent.status}
                                    onChange={(e) => updateAgentStatus.mutate({ id: agent.id, status: e.target.value as any })}
                                    className="bg-white/10 border border-white/20 text-gray-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400/50"
                                  >
                                    <option value="new">Шинэ</option>
                                    <option value="link_sent">Линк явуулсан</option>
                                    <option value="active">Идэвхтэй</option>
                                    <option value="cancelled">Цуцлах</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <Users className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Аффилиате агент бүртгэлгүй байна</p>
                      <p className="text-gray-500 text-sm mt-1">/affiliate хуудсаар бүртгэлсэн агентууд энд харагдана</p>
                      <button
                        onClick={() => setShowAddAgent(true)}
                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 text-sm font-medium rounded-lg transition-colors mx-auto"
                      >
                        <UserPlus className="w-4 h-4" />
                        Гараар агент нэмэх
                      </button>
                    </div>
                  )}

                  {/* Agent Email Compose Dialog */}
                  {showAgentEmail && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
                        style={{ background: "#0F1A2E", border: "1px solid rgba(255,255,255,0.1)" }}
                      >
                        <div className="flex items-center justify-between mb-5">
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-blue-400" />
                            <h3 className="text-white font-semibold text-lg">Агентуудрүү мэйл явуулах</h3>
                          </div>
                          <button onClick={() => setShowAgentEmail(false)} className="text-gray-400 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Recipient type */}
                        <div className="mb-4">
                          <label className="block text-gray-400 text-xs mb-2">Хүлээн авагчид</label>
                          <div className="flex gap-2 flex-wrap">
                            {(["all", "active", "new", "link_sent"] as const).map((t) => (
                              <button
                                key={t}
                                onClick={() => setAgentEmailRecipientType(t)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  agentEmailRecipientType === t
                                    ? "bg-blue-500/20 text-blue-300 border border-blue-500/40"
                                    : "bg-white/5 text-gray-400 border border-white/10 hover:text-white"
                                }`}
                              >
                                {{ all: `Бүгд (${agentList?.length || 0})`, active: "Идэвхтэй", new: "Шинэ", link_sent: "Линк явуулсан" }[t]}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Subject */}
                        <div className="mb-4">
                          <label className="block text-gray-400 text-xs mb-1.5">Гарчиг <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={agentEmailSubject}
                            onChange={(e) => setAgentEmailSubject(e.target.value)}
                            placeholder="Имэйлийн гарчиг..."
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-400/50"
                          />
                        </div>

                        {/* Body */}
                        <div className="mb-4">
                          <label className="block text-gray-400 text-xs mb-1.5">Агуулга <span className="text-red-400">*</span></label>
                          <textarea
                            value={agentEmailBody}
                            onChange={(e) => setAgentEmailBody(e.target.value)}
                            placeholder="Имэйлийн агуулга..."
                            rows={6}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-400/50 resize-none"
                          />
                          <p className="text-gray-500 text-xs mt-1">Тайлбар агентын нэр автоматаар ороно. Жшээ: "Сайн байна уу, Батбаяр!"</p>
                        </div>

                        {/* Result */}
                        {agentEmailResult && (
                          <div className={`rounded-xl p-3 mb-4 ${
                            agentEmailResult.failed === 0 ? "bg-green-500/10 border border-green-500/30" : "bg-amber-500/10 border border-amber-500/30"
                          }`}>
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className={`w-4 h-4 ${agentEmailResult.failed === 0 ? "text-green-400" : "text-amber-400"}`} />
                              <span className="text-white text-sm">
                                {agentEmailResult.sent}/{agentEmailResult.total} имэйл амжилттай илгээгдлээ
                                {agentEmailResult.failed > 0 && ` · ${agentEmailResult.failed} амжилтгүй`}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Error */}
                        {agentEmailError && (
                          <p className="text-red-400 text-xs mb-4">{agentEmailError}</p>
                        )}

                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowAgentEmail(false)}
                            className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10 rounded-xl text-sm font-medium transition-colors"
                          >
                            Хаах
                          </button>
                          <button
                            onClick={() => {
                              if (!agentEmailSubject.trim() || !agentEmailBody.trim()) return;
                              setAgentEmailError("");
                              sendAgentEmail.mutate({
                                recipientType: agentEmailRecipientType,
                                subject: agentEmailSubject,
                                body: agentEmailBody,
                              });
                            }}
                            disabled={sendAgentEmail.isPending || !agentEmailSubject.trim() || !agentEmailBody.trim()}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {sendAgentEmail.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Илгээх
                          </button>
                        </div>
                      </motion.div>
                    </div>
                  )}

                  {/* Add Agent Dialog */}
                  {showAddAgent && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="w-full max-w-lg rounded-2xl p-6"
                        style={{ background: "#0F1A2E", border: "1px solid rgba(255,255,255,0.1)" }}
                      >
                        <div className="flex items-center justify-between mb-5">
                          <div className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-amber-400" />
                            <h3 className="text-white font-semibold text-lg">Агент гараар бүртгэх</h3>
                          </div>
                          <button onClick={() => setShowAddAgent(false)} className="text-gray-400 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          {/* Email - required */}
                          <div>
                            <label className="block text-gray-400 text-xs mb-1.5">Имэйл <span className="text-red-400">*</span></label>
                            <input
                              value={newAgent.email}
                              onChange={(e) => setNewAgent(p => ({ ...p, email: e.target.value }))}
                              placeholder="agent@example.com"
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/50"
                            />
                          </div>
                          {/* Name */}
                          <div>
                            <label className="block text-gray-400 text-xs mb-1.5">Нэр <span className="text-gray-500 text-xs">(заавал биш)</span></label>
                            <input
                              value={newAgent.name}
                              onChange={(e) => setNewAgent(p => ({ ...p, name: e.target.value }))}
                              placeholder="Овогийн нэр"
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/50"
                            />
                          </div>
                          {/* Phone */}
                          <div>
                            <label className="block text-gray-400 text-xs mb-1.5">Утасны дугаар <span className="text-gray-500 text-xs">(заавал биш)</span></label>
                            <input
                              value={newAgent.phone}
                              onChange={(e) => setNewAgent(p => ({ ...p, phone: e.target.value }))}
                              placeholder="99001234"
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/50"
                            />
                          </div>
                          {/* Plan & Price */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-gray-400 text-xs mb-1.5">Төлөвлөгө <span className="text-gray-500 text-xs">(заавал биш)</span></label>
                              <select
                                value={newAgent.plan}
                                onChange={(e) => setNewAgent(p => ({ ...p, plan: e.target.value }))}
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-400/50"
                              >
                                <option value="" className="bg-gray-900">Сонгох...</option>
                                <option value="6 сар" className="bg-gray-900">6 сар</option>
                                <option value="12 сар" className="bg-gray-900">12 сар</option>
                                <option value="Lifetime" className="bg-gray-900">Lifetime</option>
                                <option value="Бусад" className="bg-gray-900">Бусад</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-gray-400 text-xs mb-1.5">Төлбөр <span className="text-gray-500 text-xs">(заавал биш)</span></label>
                              <input
                                value={newAgent.price}
                                onChange={(e) => setNewAgent(p => ({ ...p, price: e.target.value }))}
                                placeholder="₮299,000"
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/50"
                              />
                            </div>
                          </div>
                          {/* Status */}
                          <div>
                            <label className="block text-gray-400 text-xs mb-1.5">Төлөв байдал</label>
                            <select
                              value={newAgent.status}
                              onChange={(e) => setNewAgent(p => ({ ...p, status: e.target.value as any }))}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-400/50"
                            >
                              <option value="new" className="bg-gray-900">Шинэ</option>
                              <option value="link_sent" className="bg-gray-900">Линк явуулсан</option>
                              <option value="active" className="bg-gray-900">Идэвхтэй</option>
                              <option value="cancelled" className="bg-gray-900">Цуцлагдсан</option>
                            </select>
                          </div>
                          {/* Admin note */}
                          <div>
                            <label className="block text-gray-400 text-xs mb-1.5">Тэмдэглэл <span className="text-gray-500 text-xs">(заавал биш)</span></label>
                            <textarea
                              value={newAgent.adminNote}
                              onChange={(e) => setNewAgent(p => ({ ...p, adminNote: e.target.value }))}
                              placeholder="Дотоод тэмдэглэл..."
                              rows={2}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/50 resize-none"
                            />
                          </div>
                        </div>

                        {/* Error */}
                        {addAgentError && (
                          <p className="mt-3 text-red-400 text-xs">{addAgentError}</p>
                        )}

                        <div className="flex gap-3 mt-6">
                          <button
                            onClick={() => setShowAddAgent(false)}
                            className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10 rounded-xl text-sm font-medium transition-colors"
                          >
                            Цуцлах
                          </button>
                          <button
                            onClick={() => {
                              if (!newAgent.email) {
                                setAddAgentError("Имэйл хаяг оруулна уу");
                                return;
                              }
                              setAddAgentError("");
                              createAgent.mutate({
                                name: newAgent.name,
                                phone: newAgent.phone,
                                email: newAgent.email,
                                plan: newAgent.plan,
                                price: newAgent.price,
                                status: newAgent.status,
                                adminNote: newAgent.adminNote,
                              });
                            }}
                            disabled={createAgent.isPending || !newAgent.email}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {createAgent.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Бүртгэх
                          </button>
                        </div>
                      </motion.div>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === "consultations" && (
                <motion.div key="consultations" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h2 className="text-white font-semibold">
                      Зөвлөгөө авах хүсэлтүүд <span className="text-gray-400 font-normal text-sm">({filteredConsultations.length})</span>
                    </h2>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Нэр, утас, имэйл..."
                        value={consultSearch}
                        onChange={(e) => setConsultSearch(e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-white text-sm focus:outline-none focus:border-violet-500 placeholder-gray-500"
                      />
                    </div>
                  </div>
                  {filteredConsultations.length > 0 ? (
                    <div className="space-y-3">
                      {filteredConsultations.map((req) => {
                        const sc = CONSULT_STATUS_LABELS[req.status] ?? CONSULT_STATUS_LABELS.new;
                        return (
                          <div key={req.id} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                            <div className="flex items-start justify-between flex-wrap gap-3">
                              <div className="flex items-start gap-3">
                                <div className="w-9 h-9 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                  <Phone className="w-4 h-4 text-violet-400" />
                                </div>
                                <div>
                                  <div className="text-white font-semibold">{req.name}</div>
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                    <a href={`tel:${req.phone}`} className="text-gray-400 text-sm hover:text-white transition-colors">{req.phone}</a>
                                    <a href={`mailto:${req.email}`} className="text-gray-400 text-sm hover:text-violet-300 transition-colors">{req.email}</a>
                                  </div>
                                  <div className="flex flex-wrap gap-2 mt-1.5">
                                    <span className="text-xs text-gray-400">{CONSULT_COUNTRY_LABELS[req.country] ?? req.country}</span>
                                    <span className="text-xs text-gray-500">·</span>
                                    <span className="text-xs text-gray-400 max-w-[200px] truncate">{req.visaCategory}</span>
                                  </div>
                                  {req.serviceName && (
                                    <div className="mt-1.5 flex items-center gap-2">
                                      <span className="text-xs text-violet-300 font-medium">{req.serviceName}</span>
                                      {req.servicePrice ? (
                                        <span className="text-xs text-emerald-400 font-semibold">₮{req.servicePrice.toLocaleString()}</span>
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1.5">
                                <span className={`text-xs font-medium px-2.5 py-1 rounded-full bg-white/10 ${sc.color}`}>{sc.label}</span>
                                <span className="text-gray-500 text-xs">{new Date(req.createdAt).toLocaleDateString("mn-MN")}</span>
                                {req.assignedTo && <span className="text-gray-400 text-xs">👤 {req.assignedTo}</span>}
                              </div>
                            </div>
                            {req.notes && (
                              <div className="mt-3 bg-white/5 rounded-xl p-3">
                                <p className="text-gray-300 text-sm">{req.notes}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <AlertCircle className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Зөвлөгөө авах хүсэлт олдсонгүй</p>
                      <p className="text-gray-500 text-sm mt-1">Хэрэглэхчид зөвлөгөө авах хүсэлтүүд энд харагдана</p>
                    </div>
                  )}
                </motion.div>
              )}

                          {activeTab === "contracts" && (
                <motion.div key="contracts" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Send Contract Form */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                      <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                        <Send className="w-4 h-4 text-blue-400" /> Гэрээ явуулах
                      </h3>
                      {contractSent && (
                        <div className="mb-4 bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-green-400 text-sm flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" /> Гэрээ амжилттай явагдлаа!
                        </div>
                      )}
                      <div className="space-y-3">
                        {/* Template selector */}
                        <div>
                          <label className="text-gray-400 text-xs mb-1 block">Загвар сонгох (зөвхөндөлтэй)</label>
                          <select
                            value={contractTemplateId ?? ""}
                            onChange={(e) => {
                              const id = e.target.value ? Number(e.target.value) : null;
                              setContractTemplateId(id);
                              if (id) {
                                const tpl = contractTemplateList.data?.find((t: any) => t.id === id);
                                if (tpl) {
                                  setContractCountry(tpl.country);
                                  setContractBodyHtml(tpl.bodyHtml);
                                  if (!contractServiceName) setContractServiceName(tpl.name);
                                }
                              }
                            }}
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                          >
                            <option value="" className="bg-[#0A1628]">— Загвар сонгох —</option>
                            {contractTemplateList.data?.map((t: any) => (
                              <option key={t.id} value={t.id} className="bg-[#0A1628]">{t.name}</option>
                            ))}
                          </select>
                        </div>
                        {/* Country */}
                        <div>
                          <label className="text-gray-400 text-xs mb-1 block">Улс *</label>
                          <select value={contractCountry} onChange={(e) => setContractCountry(e.target.value)}
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500">
                            {Object.entries({ usa: "🇺🇸 АНУ", australia: "🇦🇺 Австрали", canada: "🇨🇦 Канад", ireland: "🇮🇪 Ирланд", schengen: "🇪🇺 Шенген", czech: "🇨🇿 Чех", newzealand: "🇳🇿 Шинэ Зеланд", other: "🌍 Бусад" }).map(([k, v]) => (
                              <option key={k} value={k} className="bg-[#0A1628]">{v}</option>
                            ))}
                          </select>
                        </div>
                        {/* Service name */}
                        <div>
                          <label className="text-gray-400 text-xs mb-1 block">Үйлчилгээний нэр *</label>
                          <input value={contractServiceName} onChange={(e) => setContractServiceName(e.target.value)}
                            placeholder="жишээ: АНУ B1/B2 визийн зуучлал"
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 placeholder-gray-500" />
                        </div>
                        {/* Body HTML */}
                        <div>
                          <label className="text-gray-400 text-xs mb-1 block">Гэрээний агуулга (HTML) *</label>
                          <textarea value={contractBodyHtml} onChange={(e) => setContractBodyHtml(e.target.value)}
                            rows={6} placeholder="Гэрээний текст энд оруулна уу..."
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 resize-none placeholder-gray-500 font-mono" />
                          <p className="text-gray-500 text-xs mt-1">Загвар ашиглахад дээр агуулга автоматаар орно. HTML тэгсэг дэмжиглэнэ.</p>
                        </div>
                        {/* Payment type */}
                        <div>
                          <label className="text-gray-400 text-xs mb-1 block">Төлбөрийн хэлбэр *</label>
                          <div className="flex gap-3">
                            {(["full", "installment"] as const).map((pt) => (
                              <button key={pt} onClick={() => setContractPaymentType(pt)}
                                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                                  contractPaymentType === pt ? "bg-blue-600 text-white" : "bg-white/10 text-gray-300 hover:bg-white/20"
                                }`}>
                                {pt === "full" ? "💳 Бүхлээр төлбөр" : "💰 Урьдчилгаа+Дараа"}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Amounts */}
                        <div className={`grid gap-3 ${contractPaymentType === "installment" ? "grid-cols-3" : "grid-cols-1"}`}>
                          <div>
                            <label className="text-gray-400 text-xs mb-1 block">Нийт дүн (₮) *</label>
                            <input type="number" value={contractTotal} onChange={(e) => setContractTotal(e.target.value)}
                              placeholder="850000"
                              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 placeholder-gray-500" />
                          </div>
                          {contractPaymentType === "installment" && (
                            <>
                              <div>
                                <label className="text-gray-400 text-xs mb-1 block">Урьдчилгаа (₮)</label>
                                <input type="number" value={contractAdvance} onChange={(e) => setContractAdvance(e.target.value)}
                                  placeholder="500000"
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 placeholder-gray-500" />
                              </div>
                              <div>
                                <label className="text-gray-400 text-xs mb-1 block">Үлдэгдэл (₮)</label>
                                <input type="number" value={contractRemaining} onChange={(e) => setContractRemaining(e.target.value)}
                                  placeholder="350000"
                                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 placeholder-gray-500" />
                              </div>
                            </>
                          )}
                        </div>
                        {/* Client email */}
                        <div>
                          <label className="text-gray-400 text-xs mb-1 block">Хэрэглэгчийн имэйл *</label>
                          <input type="email" value={contractClientEmail} onChange={(e) => setContractClientEmail(e.target.value)}
                            placeholder="client@example.com"
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 placeholder-gray-500" />
                        </div>
                        {/* Send button */}
                        <button
                          onClick={() => {
                            if (!contractServiceName || !contractBodyHtml || !contractClientEmail || !contractTotal) return;
                            sendContractMutation.mutate({
                              templateId: contractTemplateId ?? undefined,
                              country: contractCountry,
                              serviceName: contractServiceName,
                              bodyHtml: contractBodyHtml,
                              paymentType: contractPaymentType,
                              totalAmount: Number(contractTotal),
                              advanceAmount: contractPaymentType === "installment" ? Number(contractAdvance) : undefined,
                              remainingAmount: contractPaymentType === "installment" ? Number(contractRemaining) : undefined,
                              clientEmail: contractClientEmail,
                            });
                          }}
                          disabled={sendContractMutation.isPending || !contractServiceName || !contractBodyHtml || !contractClientEmail || !contractTotal}
                          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors"
                        >
                          {sendContractMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Гэрээ явуулах
                        </button>
                        {sendContractMutation.error && (
                          <p className="text-red-400 text-xs mt-1">{sendContractMutation.error.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Contracts List */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                      <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-300" /> Явуулсан гэрээнүүд
                      </h3>
                      {/* Status filter */}
                      <div className="flex gap-2 mb-4 flex-wrap">
                        {(["all", "sent", "signed", "cancelled"] as const).map((s) => (
                          <button key={s} onClick={() => setContractStatusFilter(s)}
                            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                              contractStatusFilter === s ? "bg-blue-600 text-white" : "bg-white/10 text-gray-400 hover:bg-white/20"
                            }`}>
                            {{ all: "Бүгд", sent: "Явуулсан", signed: "Гарын үсэг зурагдсан", cancelled: "Цуцлагдсан" }[s]}
                          </button>
                        ))}
                      </div>
                      {contractList.isLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                      ) : contractList.data?.length === 0 ? (
                        <div className="text-center py-12">
                          <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">Гэрээ олдсонгүй</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                          {contractList.data?.map((c: any) => (
                            <div key={c.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-white text-sm font-semibold truncate">{c.serviceName}</p>
                                  <p className="text-gray-400 text-xs mt-0.5">{c.clientEmail}</p>
                                  <p className="text-gray-500 text-xs">{new Date(c.createdAt).toLocaleDateString("mn-MN")} · {c.totalAmount.toLocaleString()}₮</p>
                                  {c.status === "signed" && c.clientName && (
                                    <p className="text-green-400 text-xs mt-1">✅ {c.clientName} — {new Date(c.signedAt).toLocaleDateString("mn-MN")}</p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    c.status === "signed" ? "bg-green-500/20 text-green-400" :
                                    c.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                                    "bg-red-500/20 text-red-400"
                                  }`}>
                                    {{ sent: "Явуулсан", signed: "Гарын үсэг зурагдсан", cancelled: "Цуцлагдсан" }[c.status as string]}
                                  </span>
                                  {c.status === "sent" && (
                                    <button
                                      onClick={() => {
                                        if (confirm("Гэрээг цуцлах уу?")) cancelContractMutation.mutate({ id: c.id });
                                      }}
                                      className="text-xs text-red-400 hover:text-red-300 transition-colors"
                                    >
                                      Цуцлах
                                    </button>
                                  )}
                                  <a href={`/contract/${c.token}`} target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
                                    Линк харах
                                  </a>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
